import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Circle, ClipboardCopy, ExternalLink, Mail, Save, Send, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { adminApi } from '@/lib/api';
import { formatDate } from '@/lib/taxonomies';
import { useSeo } from '@/lib/seo';
import { ADMIN } from '@/constants/testIds';

function formatYearMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
}

export default function RecapsAdminPage() {
  useSeo({ title: 'Recaps | Admin' });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMonth, setOpenMonth] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [intro, setIntro] = useState('');
  const [status, setStatus] = useState('draft');
  const [emailStatus, setEmailStatus] = useState(null);
  const [broadcastConfirmOpen, setBroadcastConfirmOpen] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi
      .listRecaps()
      .then(setRows)
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
    adminApi.emailStatus().then(setEmailStatus).catch(() => setEmailStatus(null));
  };
  useEffect(load, []);

  const openEditor = (yearMonth) => {
    setOpenMonth(yearMonth);
    setDetail(null);
    setDetailLoading(true);
    adminApi
      .getRecap(yearMonth)
      .then((d) => {
        setDetail(d);
        setIntro(d.recap.intro || '');
        setStatus(d.recap.status || 'draft');
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to open'))
      .finally(() => setDetailLoading(false));
  };

  const closeEditor = () => {
    setOpenMonth(null);
    setDetail(null);
    setSaving(false);
  };

  const save = async () => {
    if (!openMonth) return;
    setSaving(true);
    try {
      await adminApi.updateRecap(openMonth, { intro, status });
      toast.success(status === 'published' ? 'Recap published' : 'Recap saved');
      closeEditor();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const copyEmailHtml = async () => {
    if (!openMonth) return;
    try {
      const res = await adminApi.recapEmail(openMonth);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(res.html);
        toast.success('Newsletter HTML copied to clipboard.');
      } else {
        // Fallback: open a blob download so the admin can grab the file
        const blob = new Blob([res.html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `recap-${openMonth}.html`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Downloaded HTML (clipboard was unavailable).');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to fetch email HTML');
    }
  };

  const broadcast = async () => {
    if (!openMonth) return;
    setBroadcasting(true);
    try {
      const res = await adminApi.broadcastRecap(openMonth, false);
      toast.success(
        `Sent to ${res.sent} subscriber${res.sent === 1 ? '' : 's'}${res.failed ? ` (${res.failed} failed)` : ''}.`
      );
      setBroadcastConfirmOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setBroadcasting(false);
    }
  };

  const publishedCount = useMemo(
    () => rows.filter((r) => r.recap_status === 'published').length,
    [rows]
  );

  const canBroadcast =
    !!emailStatus?.enabled && !emailStatus?.is_sandbox && status === 'published';
  const broadcastBlockedReason = !emailStatus?.enabled
    ? 'Email not configured.'
    : emailStatus?.is_sandbox
    ? 'Sandbox sender — verify your domain in Resend, then set a Sender Email in Site Config.'
    : status !== 'published'
    ? 'Publish the recap first, then broadcast.'
    : '';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="kicker">Recaps</p>
          <h1 className="font-serif text-3xl tracking-tight">Monthly digests.</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            Every month with at least one Field Note becomes a shareable public recap page. Add a
            short intro, then flip it live.
          </p>
        </div>
        <Badge variant="secondary" className="font-normal">
          {publishedCount} published · {rows.length} total
        </Badge>
      </div>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Intro</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10">
                    <div className="space-y-3">
                      {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-6 w-full" />
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]"
                  >
                    Publish a Field Note dated in a given month and a recap row appears here.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((r) => (
                  <TableRow key={r.year_month} data-testid={ADMIN.recapRow}>
                    <TableCell className="font-medium">
                      {formatYearMonth(r.year_month)}
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">
                        {r.year_month}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {r.note_count_published}
                        <span className="text-[hsl(var(--muted-foreground))]">
                          {' '}
                          / {r.note_count_total} pub
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {r.has_intro ? (
                        <CheckCircle2 size={16} className="text-[hsl(var(--primary))]" />
                      ) : (
                        <Circle size={16} className="text-[hsl(var(--muted-foreground))]" />
                      )}
                    </TableCell>
                    <TableCell>
                      {r.recap_status === 'published' ? (
                        <Badge className="font-normal">Live</Badge>
                      ) : (
                        <Badge variant="secondary" className="font-normal">
                          Draft
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">
                      {r.updated_at ? formatDate(r.updated_at) : '—'}
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <div className="inline-flex items-center gap-2">
                        {r.recap_status === 'published' && (
                          <a
                            href={`/recap/${r.year_month}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                          >
                            View <ExternalLink size={12} />
                          </a>
                        )}
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => openEditor(r.year_month)}
                        >
                          Manage
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!openMonth} onOpenChange={(o) => (!o ? closeEditor() : null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {openMonth ? formatYearMonth(openMonth) : ''} recap
            </DialogTitle>
            <DialogDescription>
              Write a short intro (2–4 sentences) that will sit above the note list on the
              public recap page, then flip the toggle to publish.
            </DialogDescription>
          </DialogHeader>

          {detailLoading || !detail ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-6 w-40" />
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <Label htmlFor="recap-intro" className="text-sm">
                  Intro copy
                </Label>
                <Textarea
                  id="recap-intro"
                  data-testid={ADMIN.recapEditIntro}
                  value={intro}
                  onChange={(e) => setIntro(e.target.value)}
                  placeholder="What was this month about? Weather, wins, a lesson learned…"
                  rows={5}
                  className="mt-1.5"
                />
                <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                  Kept short — the public page shows this above the note cards.
                </p>
              </div>

              <div className="flex items-center gap-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.35)] p-3">
                <Switch
                  id="recap-publish"
                  data-testid={ADMIN.recapPublishToggle}
                  checked={status === 'published'}
                  onCheckedChange={(v) => setStatus(v ? 'published' : 'draft')}
                />
                <Label htmlFor="recap-publish" className="cursor-pointer">
                  <div className="text-sm font-medium">Publish recap</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    Toggle live so the page appears at /recap/{openMonth}.
                  </div>
                </Label>
              </div>

              <div>
                <p className="kicker">Included Field Notes ({detail.notes.length})</p>
                {detail.notes.length === 0 ? (
                  <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
                    No Field Notes dated in this month yet. Draft notes will appear here in the
                    admin, but only published notes show on the public recap page.
                  </p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {detail.notes.map((n) => (
                      <li
                        key={n.id}
                        className="flex items-center justify-between gap-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate">{n.title}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            {formatDate(n.date)}
                          </div>
                        </div>
                        {n.status === 'published' ? (
                          <Badge variant="secondary" className="font-normal">
                            Published
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="font-normal">
                            Draft
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.2)] p-4">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-[hsl(var(--primary))]" />
                  <p className="kicker !mb-0">Newsletter</p>
                </div>
                <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                  Copy an email-ready HTML version of this recap, or broadcast it directly to your
                  active subscribers via Resend.
                </p>
                {!canBroadcast && broadcastBlockedReason && (
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-50/60 p-2.5 text-xs text-amber-900">
                    <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                    <span>{broadcastBlockedReason}</span>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={copyEmailHtml}
                    data-testid="recap-copy-html-button"
                  >
                    <ClipboardCopy size={14} className="mr-1.5" /> Copy HTML
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setBroadcastConfirmOpen(true)}
                    disabled={!canBroadcast}
                    data-testid="recap-broadcast-button"
                  >
                    <Send size={14} className="mr-1.5" /> Broadcast to subscribers
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeEditor} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || detailLoading} data-testid={ADMIN.recapSaveButton}>
              <Save size={14} className="mr-1.5" />
              {saving ? 'Saving…' : 'Save recap'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={broadcastConfirmOpen} onOpenChange={setBroadcastConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Broadcast this recap now?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send the email version of{' '}
              <strong>{openMonth ? openMonth : ''}</strong> to every active subscriber from{' '}
              <code>{emailStatus?.sender_email || 'the configured sender'}</code>. This action can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={broadcasting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={broadcast}
              disabled={broadcasting}
              data-testid="recap-broadcast-confirm"
            >
              <Send size={14} className="mr-1.5" />
              {broadcasting ? 'Sending…' : 'Send broadcast'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
